// athlete_career / athlete_preferences / athlete_tracking hooks.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../../api/client";
import { ME_QUERY_KEY } from "./useMe";

const latest = (data) => data?.items?.[0] ?? null;

export const useAthleteCareer = (athleteId, options = {}) =>
  useQuery({
    queryKey: ["athlete-career", athleteId],
    queryFn: () =>
      apiFetch("/athlete-career", {
        query: { athlete_id: athleteId, sort: "created_at", order: "desc", pageSize: 1 },
      }),
    select: latest,
    enabled: Boolean(athleteId) && (options.enabled ?? true),
  });

export const useCreateAthleteCareer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => apiFetch("/athlete-career", { method: "POST", body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["athlete-career"] });
      queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY }); // needsEvaluation flips
    },
  });
};

export const useAthletePreferences = (athleteId, options = {}) =>
  useQuery({
    queryKey: ["athlete-preferences", athleteId],
    queryFn: () =>
      apiFetch("/athlete-preferences", {
        query: { athlete_id: athleteId, sort: "created_at", order: "desc", pageSize: 1 },
      }),
    select: latest,
    enabled: Boolean(athleteId) && (options.enabled ?? true),
  });

export const useCreateAthletePreferences = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => apiFetch("/athlete-preferences", { method: "POST", body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["athlete-preferences"] }),
  });
};

// Tracking rows are one per athlete per local day, and every write changes one
// section of that day's metrics on the server (never the whole blob), so two
// devices can't erase each other's changes.

const TRACKING_KEY = ["athlete-tracking"];

export const athleteTrackingDayKey = (athleteId, date) => [
  "athlete-tracking",
  "day",
  athleteId,
  date,
];

// Newest value of each metrics section, each from its own newest row
// ({ record_date, metrics, dates }). Dashboard widgets and the training form
// read last-known values from here. Athletes always get their own; scouts pass
// the athlete they're viewing.
export const useLatestAthleteTracking = (athleteId, options = {}) =>
  useQuery({
    queryKey: ["athlete-tracking", "latest", athleteId],
    queryFn: () =>
      apiFetch("/athlete-tracking/latest", { query: { athlete_id: athleteId } }),
    enabled: Boolean(athleteId) && (options.enabled ?? true),
  });

// One day's row (null when nothing was logged that day).
export const useAthleteTrackingDay = (athleteId, date, options = {}) =>
  useQuery({
    queryKey: athleteTrackingDayKey(athleteId, date),
    queryFn: () =>
      apiFetch("/athlete-tracking", {
        query: { athlete_id: athleteId, record_date: date, pageSize: 1 },
      }),
    select: latest,
    enabled: Boolean(athleteId && date) && (options.enabled ?? true),
  });

export const useAthleteTrackingList = (athleteId, params = {}, options = {}) =>
  useQuery({
    queryKey: ["athlete-tracking", "list", athleteId, params],
    queryFn: () =>
      apiFetch("/athlete-tracking", {
        query: {
          athlete_id: athleteId,
          sort: "record_date",
          order: "desc",
          pageSize: params.pageSize ?? 30,
          ...params,
        },
      }),
    enabled: Boolean(athleteId) && (options.enabled ?? true),
  });

// Every day write returns the saved row: show it at once, then refetch the
// rest (latest values, lists) in the background.
const useTrackingDayMutation = (mutationFn) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (row) => {
      if (row?.athlete_id && row?.record_date) {
        queryClient.setQueryData(athleteTrackingDayKey(row.athlete_id, row.record_date), {
          items: [row],
          total: 1,
          page: 1,
          pageSize: 1,
          pages: 1,
        });
      }
      queryClient.invalidateQueries({ queryKey: TRACKING_KEY });
    },
  });
};

const dayPath = (date) => `/athlete-tracking/by-date/${date}`;

// Meals stack: a second snack is added next to the first, never replacing it.
export const useAddMeal = () =>
  useTrackingDayMutation(({ date, meal }) =>
    apiFetch(`${dayPath(date)}/meals`, { method: "POST", body: meal }),
  );

export const useUpdateMeal = () =>
  useTrackingDayMutation(({ date, mealId, changes }) =>
    apiFetch(`${dayPath(date)}/meals/${mealId}`, { method: "PATCH", body: changes }),
  );

export const useDeleteMeal = () =>
  useTrackingDayMutation(({ date, mealId }) =>
    apiFetch(`${dayPath(date)}/meals/${mealId}`, { method: "DELETE" }),
  );

// The check-in answers; the server calculates the score and recommendations.
export const useSaveDailyStatus = () =>
  useTrackingDayMutation(({ date, answers }) =>
    apiFetch(`${dayPath(date)}/daily-status`, { method: "PUT", body: answers }),
  );

// Replaces only the top-level sections sent (e.g. { training }).
export const usePatchTrackingDay = () =>
  useTrackingDayMutation(({ date, metrics, notes }) =>
    apiFetch(dayPath(date), { method: "PATCH", body: { metrics, notes } }),
  );
